import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {CoachesItem, EnabledStatus, IAccountManager, IAccountManagerDetails, ListResponse} from '@/models';
import {changeEnabledStatus, displaySuccessEditOrCreateMessage, http} from '@/utils';
import {handleApiCallInStore, updateItemById} from './utils';

import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {message} from 'antd';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';

export interface IAccountManagerStore {
  accountManagerList: IAccountManager[];
  accountManager: IAccountManagerDetails;
  availableCoachesList: CoachesItem[];
  accountManagerCoaches: CoachesItem[];
  nbHits: number;
  getAccountManagerList: (params, onSuccess?: (items: IAccountManager[]) => void) => void;
  changeStatus: (payload: EnabledStatus) => void;
  getAccountById: (id: string) => void;
  getAccountManagerCoaches: (id: string, onSuccess: (data: CoachesItem[]) => void) => void;
  getAvailableCoachesList: (onSuccess: (data: CoachesItem[]) => void) => void;
  removeAccountManagerCoach: (id: string, onSuccess: () => void) => void;
  createOrUpdateAccountManager: (payload, onSuccess?: () => void) => void;
}

export const useAccountManagerStore = create(
  persist<IAccountManagerStore>(
    (set, get) => ({
      accountManagerList: [],
      accountManagerCoaches: [],
      accountManager: undefined,
      availableCoachesList: [],
      nbHits: 0,
      getAccountManagerList: async (params, onSuccess) => {
        handleApiCallInStore(LoadingKeys.AccountManager, async () => {
          const {data} = await http.get<ListResponse<IAccountManager>>(API_ENDPOINTS.accountManager, {
            params,
          } as AxiosRequestConfig);
          const {items = [], nbHits = 0} = data || {};
          if (onSuccess) {
            onSuccess(items);
          }
          set(() => ({accountManagerList: items, nbHits}));
        });
      },
      getAccountManagerCoaches: async (id, onSuccess) => {
        handleApiCallInStore(LoadingKeys.getAccountManagerCoaches, async () => {
          const {data} = await http.get<ListResponse<CoachesItem>>(`${API_ENDPOINTS.accountManager}/${id}/coaches`);
          const {items = [], nbHits = 0} = data || {};
          if (onSuccess) {
            onSuccess(items);
          }

          set(() => ({accountManagerCoaches: items, nbHits}));
        });
      },
      removeAccountManagerCoach: async (id, onSuccess) => {
        handleApiCallInStore(LoadingKeys.removeAccountManagerCoach, async () => {
          if (get().accountManagerCoaches.find((item) => item.objectID === id || item.accountManagerId === id)) {
            await http.put(`${API_ENDPOINTS.accountManager}/coaches/${id}/remove`);
          }

          set(({accountManagerCoaches}) => ({
            accountManagerCoaches: accountManagerCoaches.filter((item) => item.objectID !== id),
          }));

          if (onSuccess) {
            onSuccess();
          }
          message.success(t('coachDeletedSuccessfully'));
        });
      },
      changeStatus: (payload: EnabledStatus) => {
        handleApiCallInStore(LoadingKeys.ChangeAccountManagerStatus, async () => {
          const {id, isEnabled} = payload;
          await http.put(`${API_ENDPOINTS.accountManager}/${id}/deactivate`, {
            id,
            isEnabled,
          });

          set(({accountManagerList}) => ({
            accountManagerList: updateItemById(accountManagerList, 'isEnabled', isEnabled, id),
          }));

          changeEnabledStatus(isEnabled, t('accountManager'));
        });
      },
      getAccountById: async (id) => {
        handleApiCallInStore(LoadingKeys.GetCoachById, async () => {
          const {data} = await http.get<IAccountManagerDetails>(`${API_ENDPOINTS.accountManager}/${id}`);
          set(() => ({accountManager: data}));
        });
      },
      getAvailableCoachesList: async (onSuccess) => {
        handleApiCallInStore(LoadingKeys.getAvailableCoachesList, async () => {
          const {data} = await http.get<{ items: CoachesItem[] }>(
            `${API_ENDPOINTS.accountManager}/coaches/autocomplete`
          );
          if (onSuccess) {
            await onSuccess(data.items);
          }
          set(() => ({availableCoachesList: data.items ?? []}));
        });
      },
      createOrUpdateAccountManager: (payload, onSuccess) => {
        handleApiCallInStore(LoadingKeys.createOrUpdateAccountManager, async () => {
          const apiLink = `${API_ENDPOINTS.accountManager}/${payload.id}/coaches/add`;

          await http.put(apiLink, {
            coaches: payload.coachesLog,
            objectID: payload.id,
          });

          if (onSuccess) {
            onSuccess();
            displaySuccessEditOrCreateMessage(!!payload.id, t('accountManager'));
          }
        });
      },
    }),
    {
      name: 'AccountManager-storage',
    }
  )
);
